/**
 * SummaryCard / SummaryCardRow — the one summary-figure card.
 *
 * Phase 22, Task 22.7. Extracted verbatim from the Billing Report's four-card
 * row, which the product owner named as the shape that reads correctly: icon
 * in a muted square, uppercase tracking-wide label, mono value, with a money
 * total visually distinguished from the counts beside it.
 *
 * WHY THIS EXISTS RATHER THAN COPIED MARKUP. Grand totals were being parked
 * wherever there was room — on the right-hand half of a month bar, for
 * instance — which is how the same figure ended up looking like three
 * different kinds of thing on three pages. A total is a summary figure; a
 * summary figure is a card.
 *
 * A CARD DOES NOT SAY WHAT IT COUNTS OVER. That is the caller's job, in the
 * `label` (and, where the page has more than one scope on screen, in a note
 * beside the row). A card row sitting under a month header describes that
 * month; a card row sitting over a filtered list describes that list; the two
 * must never be mistaken for each other, and only the label can prevent it.
 */
import React from 'react';
import { Card, CardContent } from './ui/card';
import { cn } from '../lib/utils';

/**
 * Tone, not colour-picking. `money` is the green treatment the Billing
 * Report's total already had; `warning` and `danger` exist for figures that
 * are an unpaid or an overdue amount, where the same neutral card would
 * under-state a fact the admin has to act on.
 */
const VARIANTS = {
    default: { card: '', iconBox: 'bg-muted', icon: '', value: '' },
    money: {
        card: 'bg-green-50',
        iconBox: 'bg-green-100',
        icon: 'text-green-600',
        value: 'text-green-600',
    },
    warning: {
        card: 'bg-amber-50',
        iconBox: 'bg-amber-100',
        icon: 'text-amber-700',
        value: 'text-amber-700',
    },
    danger: {
        card: 'bg-red-50',
        iconBox: 'bg-red-100',
        icon: 'text-destructive',
        value: 'text-destructive',
    },
};

export function SummaryCard({
    icon: Icon,
    label,
    value,
    hint = null,
    variant = 'default',
    className,
    valueTestId,
    'data-testid': testId,
}) {
    const tone = VARIANTS[variant] || VARIANTS.default;

    return (
        <Card
            className={cn('border border-border shadow-none', tone.card, className)}
            data-testid={testId}
        >
            <CardContent className="p-4">
                <div className="flex items-center gap-3">
                    {Icon && (
                        <div className={cn('p-2 shrink-0', tone.iconBox)}>
                            <Icon className={cn('w-5 h-5', tone.icon)} />
                        </div>
                    )}
                    <div className="min-w-0 overflow-hidden">
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">
                            {label}
                        </p>
                        <p
                            className={cn(
                                'font-mono text-lg sm:text-2xl font-bold break-all',
                                tone.value
                            )}
                            data-testid={valueTestId}
                        >
                            {value}
                        </p>
                        {hint && (
                            <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

/**
 * The row the cards sit in. Two columns on a phone, four from `sm` up — the
 * Billing Report's existing grid, so an admin who already knows that page
 * meets the same layout everywhere else.
 */
export function SummaryCardRow({ children, className, 'data-testid': testId }) {
    return (
        <div
            className={cn('grid grid-cols-2 sm:grid-cols-4 gap-4', className)}
            data-testid={testId}
        >
            {children}
        </div>
    );
}

export default SummaryCard;
